/**
 * 大きいカレンダー（2026-09-23 射場さん：請求予定日のカレンダーが小さい）。
 * ブラウザの標準のカレンダー（input type=date の showPicker）は大きさを変えられないので、自前で出す。
 * 日の枠は 50×48px・文字 19px（docs/10 §3 押すところ 44px 以上）。押したボタンの下（下に入らなければ上）に出す。
 * ←→ で1日、↑↓ で1週、PageUp／PageDown で1か月。Enter で決める、Esc・外を押すと閉じる
 *
 * variant="field"（日付の欄 DateInput の窓、受け取った書類 S3＝../eigyouryoku/docs/13 §4.5・★10）：幅 330px・マス 44px・影 --shadow-pop・角丸 12px。
 *   前後の月の日も薄墨で並べる（押せる）・選んだ日は藍の塗り・今日は藍の輪・下に「今日」（と、消せる欄なら「日付削除」）。欄の左に揃えて下に出す
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const WD = ["日", "月", "火", "水", "木", "金", "土"];
const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s: string | null) => (s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : null);
const addDays = (s: string, n: number) => {
  const d = parse(s)!;
  d.setDate(d.getDate() + n);
  return ymd(d);
};
const addMonths = (s: string, n: number) => {
  const d = parse(s)!;
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  d.setDate(Math.min(day, new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()));
  return ymd(d);
};

export function CalendarPop({
  value,
  anchor,
  label,
  onPick,
  onClose,
  variant = "big",
  clearable = true,
}: {
  value: string | null;
  anchor: HTMLElement;
  label: string;
  onPick: (v: string | null) => void;
  onClose: () => void;
  /** big＝今までの大きいカレンダー（請求予定日・工程表）／field＝日付の欄の窓（DateInput） */
  variant?: "big" | "field";
  /** 「日付削除」を出すか（値があるときだけ出る） */
  clearable?: boolean;
}) {
  const field = variant === "field";
  const today = ymd(new Date());
  const [focus, setFocus] = useState<string>(parse(value) ? value! : today);
  const f = parse(focus)!;
  const y = f.getFullYear();
  const m = f.getMonth();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // 押したボタンの下（入らなければ上）に置く。画面がスクロールしてもボタンについていく
  const place = () => {
    const el = ref.current;
    if (!el) return;
    const r = anchor.getBoundingClientRect();
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const left = Math.max(8, Math.min(field ? r.left : r.right - w, window.innerWidth - w - 8));
    const below = r.bottom + 6;
    const top = below + h > window.innerHeight - 8 ? Math.max(8, r.top - h - 6) : below;
    setPos((p) => (p && p.top === top && p.left === left ? p : { top, left }));
  };
  useLayoutEffect(place, [anchor, y, m]);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || anchor.contains(t)) return;
      onClose();
    };
    const onMove = () => place();
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor, onClose]);
  // 開いたとき・動かしたとき、その日にフォーカス（画面は動かさない＝スクロールで閉じないように）
  const ready = pos !== null;
  useEffect(() => {
    if (!ready) return;
    ref.current?.querySelector<HTMLButtonElement>(`button[data-date="${focus}"]`)?.focus({ preventScroll: true });
  }, [focus, ready]);

  const first = new Date(y, m, 1).getDay();
  const days = new Date(y, m + 1, 0).getDate();
  // マスの日付（YYYY-MM-DD）。big は前後の月を空のマスに、field は前後の月の日も並べる（薄墨）
  const at = (d: Date) => ymd(d);
  const cells: (string | null)[] = [
    ...Array.from({ length: first }, (_, i) => (field ? at(new Date(y, m, i - first + 1)) : null)),
    ...Array.from({ length: days }, (_, i) => at(new Date(y, m, i + 1))),
  ];
  for (let k = 1; cells.length % 7; k++) cells.push(field ? at(new Date(y, m + 1, k)) : null);
  const rows: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));

  const onKey = (e: React.KeyboardEvent) => {
    const k = e.key;
    const move =
      k === "ArrowLeft" ? addDays(focus, -1)
      : k === "ArrowRight" ? addDays(focus, 1)
      : k === "ArrowUp" ? addDays(focus, -7)
      : k === "ArrowDown" ? addDays(focus, 7)
      : k === "PageUp" ? addMonths(focus, -1)
      : k === "PageDown" ? addMonths(focus, 1)
      : null;
    if (move) {
      e.preventDefault();
      setFocus(move);
    } else if (k === "Escape") {
      e.preventDefault();
      onClose();
      // 押したボタンに戻す（日付の欄 DateInput は外枠が anchor なので、その中の欄へ）
      (anchor.matches("input,button,select,[tabindex]") ? anchor : anchor.querySelector<HTMLElement>("input,button"))?.focus();
    }
  };

  return createPortal(
    <div ref={ref} className={"calpop" + (field ? " is-field" : "")} role="dialog" aria-label={label} style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999 }} onKeyDown={onKey}>
      <div className="calhead">
        <button type="button" aria-label="前の月" onClick={() => setFocus(addMonths(focus, -1))}>‹</button>
        <b aria-live="polite">{y}年{m + 1}月</b>
        <button type="button" aria-label="次の月" onClick={() => setFocus(addMonths(focus, 1))}>›</button>
      </div>
      <table className="calgrid" role="grid">
        <thead>
          <tr>
            {WD.map((w, i) => (
              <th key={w} scope="col" className={i === 0 ? "sun" : i === 6 ? "sat" : undefined}>{w}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((v, ci) => {
                if (v == null) return <td key={ci} />;
                const dt = parse(v)!;
                const out = dt.getMonth() !== m; // 前後の月の日（field だけ）
                const cls = [ci === 0 ? "sun" : ci === 6 ? "sat" : "", v === today ? "today" : "", out ? "is-out" : ""].filter(Boolean).join(" ");
                return (
                  <td key={ci}>
                    <button type="button" className={cls || undefined} data-date={v} tabIndex={v === focus ? 0 : -1} aria-pressed={v === value} aria-label={`${dt.getFullYear()}年${dt.getMonth() + 1}月${dt.getDate()}日${v === today ? "（今日）" : ""}`} onClick={() => onPick(v)}>
                      {dt.getDate()}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="calfoot">
        <button type="button" className="small" onClick={() => onPick(today)}>今日</button>
        {value && clearable ? <button type="button" className="small" onClick={() => onPick(null)}>日付削除</button> : null}
        <span className="sp" />
        {field ? null : <button type="button" className="small" onClick={onClose}>閉じる</button>}
      </div>
    </div>,
    document.body,
  );
}
